import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_LABELS = {
  signup_verify_email: 'New signup → send verification email',
  verification_success_welcome: 'Email verified → send welcome',
  resend_verification: 'Resend requested → send verification link',
  order_paid_confirmation: 'Order paid → send confirmation',
  investment_approved: 'Investment approved → notify investor',
  investment_rejected: 'Investment rejected → notify investor',
  kyc_approved: 'KYC approved → notify investor',
  kyc_rejected: 'KYC rejected → notify investor',
  password_reset: 'Password reset requested → send reset link',
};

const AutomationTab = ({ templates }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');

  const fetch = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-automation`);
      setRules(res.data.rules || []);
    } catch (err) {
      toast.error('Failed to load automation rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const updateRule = async (eventKey, patch) => {
    setSavingKey(eventKey);
    try {
      await axios.put(`${API}/admin/email-automation/${eventKey}`, patch);
      toast.success('Automation updated');
      await fetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setSavingKey('');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="automation-tab">
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 flex items-start gap-3">
          <Zap className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold">How automation works</p>
            <p className="text-xs">
              When an event below is enabled, its email is sent automatically. Toggle off to suppress
              system emails for that event, or re-assign to a different template.
            </p>
          </div>
        </CardContent>
      </Card>

      {rules.map((rule) => (
        <Card key={rule.event_key} data-testid={`automation-row-${rule.event_key}`}>
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex-1">
              <p className="font-semibold text-sm">
                {EVENT_LABELS[rule.event_key] || rule.event_key}
              </p>
              <p className="text-xs text-gray-500 font-mono mt-0.5">{rule.event_key}</p>
            </div>

            <div className="flex items-center gap-3">
              <select
                value={rule.template_key}
                onChange={(e) => updateRule(rule.event_key, { template_key: e.target.value })}
                className="text-sm border rounded-md px-2 py-1.5"
                disabled={savingKey === rule.event_key}
                data-testid={`automation-template-select-${rule.event_key}`}
              >
                {templates.map((t) => (
                  <option key={t.key} value={t.key}>{t.name}</option>
                ))}
              </select>

              <label
                className="relative inline-flex items-center cursor-pointer"
                data-testid={`automation-toggle-${rule.event_key}`}
              >
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={rule.enabled}
                  onChange={(e) => updateRule(rule.event_key, { enabled: e.target.checked })}
                  disabled={savingKey === rule.event_key}
                />
                <div className="w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-green-600 transition-colors" />
                <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
              </label>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default AutomationTab;
